// Narration preference + playback engine for intervention task text.
//
// Three things live here so the narration feature is self-contained:
//   1. A localStorage-backed boolean preference ("narrate tasks aloud")
//      — defaults to TRUE so narration just-works on first install.
//   2. A localStorage-backed shared volume (0..1) for narration. This
//      volume is unified with the breath-cue voice volume in
//      soundEngine via getSoundPrefs/setSoundPrefs.voiceVolume, so
//      changing the slider in Settings moves both at once.
//   3. A tiny shared <audio> element that plays a sequence of clips
//      with a configurable pause between consecutive clips, so the
//      frontend can deliver "paragraph 1 ... 1.5s of quiet ...
//      paragraph 2" without server-side audio editing.

import { getSoundPrefs, setSoundPrefs } from "./soundEngine";

const PREF_KEY = "bc_narrate_tasks";
// Legacy: when we first shipped narration we wrote the volume to
// bc_narrate_volume. New writes go to soundPrefs.voiceVolume so the
// two sliders share one source of truth. We still READ the legacy key
// once on first load and migrate it forward.
const LEGACY_VOLUME_KEY = "bc_narrate_volume";
const DEFAULT_VOLUME = 0.85;
let sharedAudio = null;
let cancelSequence = null;
let isSequencePlaying = false;

/** Returns true if the user has opted in to auto-narration.
 *  Defaults to FALSE — narration is opt-in via the Settings toggle or
 *  via the per-task speaker button. */
export function getNarrationPref() {
  try {
    return localStorage.getItem(PREF_KEY) === "1";
  } catch {
    return false;
  }
}

/** Persists the auto-narration preference. */
export function setNarrationPref(enabled) {
  try {
    if (enabled) localStorage.setItem(PREF_KEY, "1");
    else localStorage.removeItem(PREF_KEY);
  } catch {
    /* private mode etc. — ignore */
  }
}

/** Returns the user's narration volume in [0..1]. The source of truth
 *  is soundPrefs.voiceVolume (unified with breath cues). Falls back to
 *  the legacy localStorage key and then to DEFAULT_VOLUME. */
export function getNarrationVolume() {
  try {
    const prefs = getSoundPrefs();
    if (prefs && typeof prefs.voiceVolume === "number") {
      return Math.max(0, Math.min(1, prefs.voiceVolume));
    }
    const raw = localStorage.getItem(LEGACY_VOLUME_KEY);
    if (raw === null) return DEFAULT_VOLUME;
    const v = Number.parseFloat(raw);
    if (Number.isNaN(v)) return DEFAULT_VOLUME;
    return Math.max(0, Math.min(1, v));
  } catch {
    return DEFAULT_VOLUME;
  }
}

/** Persists the narration volume and applies it to the live audio
 *  element immediately. Writes to soundPrefs.voiceVolume so the breath-
 *  cue voice and the narration voice always match. */
export function setNarrationVolume(volume) {
  const clamped = Math.max(0, Math.min(1, Number(volume) || 0));
  try {
    const prefs = getSoundPrefs();
    setSoundPrefs({ ...prefs, voiceVolume: clamped });
  } catch {
    /* ignore */
  }
  if (sharedAudio) sharedAudio.volume = clamped;
}

function getAudio() {
  if (sharedAudio) return sharedAudio;
  const a = new Audio();
  a.preload = "auto";
  // NOTE: intentionally no `a.crossOrigin` here. Setting it to
  // "anonymous" makes iOS Capacitor WKWebView do a CORS preflight on
  // every narration MP3 fetch, and if the response doesn't include the
  // right Access-Control-Allow-Origin (e.g. the Capacitor localhost
  // scheme), the <audio> element rejects the load silently and the
  // narration button appears "stuck". We never read the audio samples,
  // so cross-origin data isn't needed — dropping the attribute makes
  // the load behave like a plain <img> across schemes.
  a.volume = getNarrationVolume();
  a.setAttribute("playsinline", "true");
  sharedAudio = a;
  return a;
}

// ============================================================================
// Audio autoplay unlock
// ----------------------------------------------------------------------------
// Browsers (especially Chrome + iOS Safari) will reject `audio.play()` with
// NotAllowedError when too much time has elapsed since the last user gesture.
// Our narration triggers a network round-trip (TTS generation) BEFORE the
// first play(), so by the time the response lands the gesture window has
// often closed — even though the user did just click Continue.
//
// `unlockAudio()` is called from inside a click handler. It plays a tiny
// silent inline buffer to satisfy the autoplay policy. Once the shared audio
// element has been played-with-gesture, subsequent play() calls succeed for
// the rest of the page session, regardless of how long fetches take.
// ============================================================================
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
// iOS Safari / Capacitor WebView occasionally *re-locks* the audio
// element after long idle periods (e.g. between task-1 and task-3 when
// the user reads slowly). We used to gate `unlockAudio` behind a
// `audioUnlocked` boolean so it only ran once — that's exactly what
// caused the "tap the speaker to start narration" trap: the first
// unlock succeeded, but by the time the second TTS response arrived
// iOS had silently re-armed the autoplay policy. Removing the gate
// means every user gesture re-primes the shared <audio>, which is
// cheap (a paused silent WAV) and keeps play() reliable across long
// intervals. Kept exported for legacy callers.
let audioUnlockPending = false;

export function unlockAudio() {
  if (audioUnlockPending) return; // avoid overlapping unlock attempts
  // Skip unlock entirely while an active narration sequence is
  // orchestrating playback — reassigning src to SILENT_WAV between
  // clips (the sleep() window inside playNarrationSequence) races with
  // the next clip's src assignment and can leave two overlapping play
  // paths on some browsers, producing a ~100-200 ms voice echo. If a
  // sequence is running, the audio element is already "primed" by the
  // in-flight play() call and doesn't need re-unlocking.
  if (isSequencePlaying) return;
  const audio = getAudio();
  // If audio is already actively playing user-visible narration, don't
  // interrupt it — that would cut the narrator off mid-word.
  if (!audio.paused && audio.src && audio.src !== SILENT_WAV) return;
  audioUnlockPending = true;
  const previousMuted = audio.muted;
  const previousSrc = audio.src;
  audio.muted = true;
  audio.src = SILENT_WAV;
  try {
    const p = audio.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = previousMuted;
        if (previousSrc && previousSrc !== SILENT_WAV) audio.src = previousSrc;
      }).catch(() => {
        audio.muted = previousMuted;
      }).finally(() => {
        audioUnlockPending = false;
      });
    } else {
      // Older browsers return undefined from play(); treat as unlocked.
      audio.pause();
      audio.muted = previousMuted;
      audioUnlockPending = false;
    }
  } catch {
    audio.muted = previousMuted;
    audioUnlockPending = false;
  }
}

// Wait for the shared audio element to finish playing the current src.
// Resolves with the reason ("ended", "error", "cancelled").
function awaitClipEnd(audio, signal) {
  return new Promise((resolve) => {
    const cleanup = () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      signal.removeEventListener("abort", onAbort);
    };
    const onEnded = () => { cleanup(); resolve({ reason: "ended" }); };
    const onError = () => { cleanup(); resolve({ reason: "error" }); };
    const onAbort = () => { cleanup(); resolve({ reason: "cancelled" }); };
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    signal.addEventListener("abort", onAbort);
  });
}

// Sleep helper that aborts cleanly when the sequence is cancelled.
function sleep(ms, signal) {
  return new Promise((resolve) => {
    const tid = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve({ reason: "ended" });
    }, ms);
    const onAbort = () => {
      clearTimeout(tid);
      resolve({ reason: "cancelled" });
    };
    signal.addEventListener("abort", onAbort);
  });
}

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Resolve a narration clip URL against the backend origin. The
 * backend returns paths like `/api/audio/narration/xxx.mp3`. On the
 * web build this resolves correctly against `document.origin` — same
 * as REACT_APP_BACKEND_URL. On the Capacitor iOS build the WebView
 * origin is `capacitor://localhost/` so a bare `/api/…` resolves to
 * the local WebView (nothing there → silent 404). We prepend the
 * backend origin explicitly so audio loads work on every platform.
 */
function resolveClipUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url; // already absolute
  if (!BACKEND_ORIGIN) return url;           // dev safety net
  return `${BACKEND_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Play a SEQUENCE of narration clips with a configurable pause
 * between each. Clips may be plain string URLs (legacy) OR objects
 * with `{ url, pause_after_ms }` — when an object is passed, the
 * `pause_after_ms` overrides the default `pauseMs` for that clip.
 *
 * @param {(string|{url:string,pause_after_ms?:number})[]} clips
 * @param {object} [opts]
 * @param {number} [opts.pauseMs=1500]  — default pause between clips
 *                                       when none is specified inline
 * @param {(info)=>void} [opts.onEnd]   — fired exactly once when the whole
 *                                        sequence finishes, errors, or is
 *                                        cancelled. `info.reason` ∈
 *                                        "ended" | "error" | "blocked" |
 *                                        "cancelled"
 */
export async function playNarrationSequence(clips, opts = {}) {
  const defaultPauseMs = typeof opts.pauseMs === "number" ? opts.pauseMs : 1500;
  const onEnd = typeof opts.onEnd === "function" ? opts.onEnd : null;
  // Cancel any in-flight sequence so we never end up with overlapping
  // narrators talking over each other. `cancelSequence` only aborts
  // the promise chain — we ALSO pause+reset the shared audio element
  // here so any bytes still buffered from the previous clip stop
  // outputting to the speaker before the new sequence assigns its src.
  if (cancelSequence) cancelSequence();
  if (sharedAudio) {
    try { sharedAudio.pause(); } catch (_) { /* ignore */ }
    try { sharedAudio.currentTime = 0; } catch (_) { /* ignore */ }
  }
  const controller = new AbortController();
  cancelSequence = () => controller.abort();
  isSequencePlaying = true;

  const audio = getAudio();
  audio.volume = getNarrationVolume();

  const finish = (info) => {
    isSequencePlaying = false;
    if (cancelSequence) cancelSequence = null;
    if (onEnd) onEnd(info);
  };

  try {
    for (let i = 0; i < clips.length; i += 1) {
      if (controller.signal.aborted) { finish({ reason: "cancelled" }); return; }
      const clip = clips[i];
      const rawUrl = typeof clip === "string" ? clip : clip?.url;
      if (!rawUrl) continue;
      const url = resolveClipUrl(rawUrl);
      // Fully quiesce the element before assigning a new src.
      // Without an explicit pause + load, some browsers (notably
      // WebKit-based ones) briefly continue playing decoded bytes
      // from the OLD clip for ~100-200 ms after `audio.src = newUrl`,
      // producing a doubled-voice echo when the OLD and NEW clips
      // are the same content (e.g. same task narrated twice in
      // quick succession). Pausing + removing the src forces the
      // decoder to drop its buffer before we assign the new source.
      try { audio.pause(); } catch (_) { /* ignore */ }
      audio.removeAttribute("src");
      try { audio.load(); } catch (_) { /* ignore */ }
      audio.src = url;
      audio.currentTime = 0;
      audio.volume = getNarrationVolume();
      try {
        await audio.play();
      } catch (err) {
        // Autoplay blocked (no recent user gesture). Bail out — UI
        // shows the "tap again" toast.
        finish({ reason: "blocked", error: err });
        return;
      }
      const res = await awaitClipEnd(audio, controller.signal);
      if (res.reason === "cancelled") { finish({ reason: "cancelled" }); return; }
      if (res.reason === "error") { finish({ reason: "error" }); return; }
      // Pause AFTER this clip — per-clip pause if provided (sentence
      // vs paragraph boundary), otherwise the default. Skip after
      // the final clip.
      if (i < clips.length - 1) {
        const perClipPause = typeof clip === "object" && typeof clip.pause_after_ms === "number"
          ? clip.pause_after_ms
          : defaultPauseMs;
        if (perClipPause > 0) {
          const slept = await sleep(perClipPause, controller.signal);
          if (slept.reason === "cancelled") { finish({ reason: "cancelled" }); return; }
        }
      }
    }
    finish({ reason: "ended" });
  } catch (err) {
    finish({ reason: "error", error: err });
  }
}

/**
 * Backwards-compatible single-clip API. Plays one URL and fires onEnd.
 */
export async function playNarration(url, onEnd) {
  return playNarrationSequence([url], { pauseMs: 0, onEnd });
}

/** Stop any in-flight narration (clip or sequence). onEnd is NOT
 *  invoked because the caller initiated the stop. */
export function stopNarration() {
  if (cancelSequence) {
    const c = cancelSequence;
    cancelSequence = null;
    c();
  }
  if (sharedAudio && !sharedAudio.paused) {
    sharedAudio.pause();
    sharedAudio.currentTime = 0;
  }
  isSequencePlaying = false;
}

/** True if narration is currently audible. */
export function isNarrationPlaying() {
  return isSequencePlaying || (sharedAudio && !sharedAudio.paused);
}
